/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import PlaylistAddIcon from '@atlaskit/icon-lab/core/playlist-add';
import Modal, {
	CloseButton,
	ModalBody,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoLogoForAppSource } from '@atlassian/conversation-assistant-chat-prompt-input/RovoLogoForAppSource';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { PromptCard } from '@atlassian/rovo-conversation-actions/prompt-cards';
import type { FilterOption } from '@atlassian/rovo-conversation-actions/types';
import type { SkillMetadataMap } from '@atlassian/rovo-skills/types';

import { usePromptLibraryFilters } from '../../../common/hooks/usePromptLibraryFilters';
import { PromptLibraryContent } from '../../../common/ui/prompt-library/prompt-library-content';
import promptLibraryMessages from '../messages';

const styles = cssMap({
	modalBodyContent: {
		paddingBlockEnd: token('space.300'),
	},
	headerContentFullWidth: {
		width: '100%',
	},
	connectorLogoWrapper: {
		height: '18px',
		width: '18px',
		borderRadius: token('radius.small'),
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		overflow: 'hidden',
	},
});

export type PromptLibraryModalProps = {
	isOpen: boolean;
	onClose: () => void;
	promptCards: PromptCard[];
	myPromptCards: PromptCard[];
	onCardSelect: (card: PromptCard) => void;
	onCreatePromptClick: () => void;
	onDeletePrompt: (promptId: string) => Promise<void>;
	onEditPrompt?: (card: PromptCard) => void;
	onDuplicatePrompt?: (card: PromptCard) => void;
	onFavoriteToggle?: (
		promptId: string,
		isFavorited: boolean,
		isUserGenerated?: boolean,
	) => Promise<void>;
	/** Bulk favorite handler for multiselect - receives array of {shareId, isFavorite} items and systemPromptCount for analytics */
	onBulkFavorite?: (
		prompts: { shareId: string; isFavorite: boolean }[],
		systemPromptCount: number,
	) => Promise<void>;
	/** Bulk delete handler - deletes multiple prompts and then refetches the list once. */
	onBulkDelete?: (promptIds: string[], systemPromptCount: number) => Promise<void>;
	/** Bulk share handler - makes prompts public before copying link. Called with prompt IDs and systemPromptCount for analytics. */
	onBulkShare?: (promptIds: string[], systemPromptCount: number) => Promise<void>;
	onSharePrompt?: (card: PromptCard) => void;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	skillMetadata?: SkillMetadataMap;
	appOptions?: Array<{ value: string; label: string; iconUrl?: string }>;
	onEmptyStateViewed?: (
		emptyStateType: 'emptyFavorites' | 'emptyMyPrompts' | 'noPromptsFromFilters',
	) => void;
	isLoading?: boolean;
};

export const PromptLibraryModal = ({
	isOpen,
	onClose,
	promptCards,
	myPromptCards,
	onCardSelect,
	onCreatePromptClick,
	onDeletePrompt,
	onEditPrompt,
	onDuplicatePrompt,
	onFavoriteToggle,
	onBulkFavorite,
	onBulkDelete,
	onBulkShare,
	onSharePrompt,
	assistanceServiceParams,
	skillMetadata,
	appOptions,
	onEmptyStateViewed,
	isLoading,
}: PromptLibraryModalProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const promptLibraryFilters = usePromptLibraryFilters(promptCards, myPromptCards);

	const connectorFilterOptions = useMemo(
		() => appOptions?.map((o) => ({ id: o.value, label: o.label })) ?? [],
		[appOptions],
	);

	const renderConnectorOptionIcon = useCallback(
		(option: FilterOption) => (
			<Box xcss={styles.connectorLogoWrapper}>
				<RovoLogoForAppSource appSource={option.id} rovoLogoProps={{ size: 'xsmall' }} />
			</Box>
		),
		[],
	);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal width="838px" onClose={onClose} shouldCloseOnEscapePress shouldCloseOnOverlayClick>
					<ModalHeader>
						<Box xcss={styles.headerContentFullWidth}>
							<Inline spread="space-between" alignBlock="center" shouldWrap={false}>
								<ModalTitle>{formatMessage(promptLibraryMessages.headerTitle)}</ModalTitle>
								<Inline space="space.100" alignBlock="center">
									<Button
										appearance="primary"
										iconBefore={PlaylistAddIcon}
										onClick={onCreatePromptClick}
									>
										{formatMessage(promptLibraryMessages.createPrompt)}
									</Button>
									<CloseButton onClick={onClose} />
								</Inline>
							</Inline>
						</Box>
					</ModalHeader>
					<ModalBody>
						<Box xcss={styles.modalBodyContent}>
							<PromptLibraryContent
								searchablePrompts={promptLibraryFilters.searchablePrompts}
								onSearchChange={promptLibraryFilters.handleSearchChange}
								selectedCategory={promptLibraryFilters.selectedCategory}
								selectedCategoryFilters={promptLibraryFilters.selectedCategoryFilters}
								onMyPromptsClick={promptLibraryFilters.handleMyPromptsClick}
								onFavoritesClick={promptLibraryFilters.handleFavoritesClick}
								showMyPromptsButton
								showFavoritesButton
								selectedUseCases={promptLibraryFilters.selectedUseCases}
								onUseCaseFilterChange={promptLibraryFilters.handleUseCaseFilterChange}
								selectedRoles={promptLibraryFilters.selectedRoles}
								onRoleFilterChange={promptLibraryFilters.handleRoleFilterChange}
								resultCount={promptLibraryFilters.resultCount}
								hasActiveFilters={promptLibraryFilters.hasActiveFilters}
								onClearFilters={promptLibraryFilters.handleClearFilters}
								cardsToShow={promptLibraryFilters.cardsToShow}
								onCardClick={onCardSelect}
								onDeletePrompt={onDeletePrompt}
								onBulkDelete={onBulkDelete}
								onEditPrompt={onEditPrompt}
								onDuplicatePrompt={onDuplicatePrompt}
								onFavoriteToggle={onFavoriteToggle}
								onBulkFavorite={onBulkFavorite}
								onBulkShare={onBulkShare}
								onSharePrompt={onSharePrompt}
								assistanceServiceParams={assistanceServiceParams}
								skillMetadata={skillMetadata}
								connectorFilter={
									connectorFilterOptions.length > 0
										? {
												options: connectorFilterOptions,
												selected: promptLibraryFilters.selectedConnectors,
												onChange: promptLibraryFilters.handleConnectorFilterChange,
												renderIcon: renderConnectorOptionIcon,
											}
										: undefined
								}
								onEmptyStateViewed={onEmptyStateViewed}
								isLoading={isLoading}
							/>
						</Box>
					</ModalBody>
				</Modal>
			)}
		</ModalTransition>
	);
};
